/* Legal pages — Privacy Policy, Refund Policy, Cancellation Policy.
   These are starting-point templates, not legal advice. Have a lawyer
   review before going to production billing in regulated markets
   (esp. EU, UK, California). The text reflects the actual stack
   (Firebase, Stripe, Sentry, web-push) and the actual refund/cancel
   behaviour wired into the app today.

   Routes:
     /privacy       → renderPrivacy
     /refunds       → renderRefunds
     /cancellation  → renderCancellation
*/

const LAST_UPDATED = "29 April 2026";
const CONTACT_EMAIL = "support@studyvet.org";
const SITE_URL = "https://studyvet.org";

function pageShell(title, body) {
  return `
    <section class="legal-page">
      <header class="legal-head">
        <a class="legal-back" href="#/landing">← StudyVET</a>
        <h1>${title}</h1>
        <p class="muted">Last updated: ${LAST_UPDATED}</p>
      </header>
      <article class="legal-body">${body}</article>
      <footer class="legal-foot muted small">
        Questions about any of this? Email
        <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a>.
      </footer>
    </section>
  `;
}

// ─── Privacy ──────────────────────────────────────────────────────────────
export function renderPrivacy(view) {
  view.innerHTML = pageShell("Privacy Policy", `
    <p>
      StudyVET (the "Service", reachable at <a href="${SITE_URL}">${SITE_URL}</a>)
      is operated by TechTastic LLC. We respect your privacy. This page explains
      what we collect, how we use it, who we share it with, and what
      rights you have over your data.
    </p>

    <h2>1. What we collect</h2>
    <ul>
      <li><strong>Account info</strong>, email, display name, password (hashed
        by Firebase Authentication; we never see your password in plaintext).
        If you sign in with Google, your Google profile email and name.</li>
      <li><strong>Study activity</strong>, questions you've answered, your
        spaced-repetition schedule, bookmarks, exam history, optional
        per-question notes you write.</li>
      <li><strong>Settings</strong>, your chosen exam date, daily target,
        avatar, theme preference, push-reminder opt-in (when applicable).</li>
      <li><strong>Payment info</strong>, handled entirely by Stripe. We do
        not see, store, or process your card number. We only receive a
        Stripe customer ID, subscription status, and current period end.</li>
      <li><strong>Push subscriptions</strong>, when you enable daily
        reminders, your browser issues an opaque endpoint URL (from FCM,
        Mozilla autopush, or Apple/Microsoft equivalents) which we store
        so the reminder server can deliver the notification.</li>
      <li><strong>Anonymous analytics</strong>, when you visit the public
        landing page we increment a daily counter and record the host of
        the referring page (e.g. "google.com"). We do not record IP
        addresses or User-Agent strings on landing-page visits.</li>
      <li><strong>Error reports</strong>, when our app crashes for you,
        a stack trace, the URL you were on, and your user ID may be sent
        to Sentry so we can fix the bug. Auth tokens and form contents
        are scrubbed before transmission.</li>
    </ul>

    <h2>2. How we use it</h2>
    <ul>
      <li>To deliver the Service: keep your progress in sync across devices,
        send you the daily reminder you've opted into, charge your
        subscription via Stripe.</li>
      <li>To improve the Service: see which features are used, fix crashes,
        understand traffic sources at the aggregate (not individual) level.</li>
      <li>To contact you about your account or billing (rare; we will
        not send marketing emails).</li>
    </ul>

    <h2>3. Who we share it with</h2>
    <p>We do not sell your data. We use the following processors, each
    bound by their own privacy commitments:</p>
    <ul>
      <li><strong>Google Firebase</strong>, authentication, database, hosting,
        cloud functions. <a href="https://firebase.google.com/support/privacy">firebase.google.com/support/privacy</a></li>
      <li><strong>Stripe, Inc.</strong>, payments. <a href="https://stripe.com/privacy">stripe.com/privacy</a></li>
      <li><strong>Functional Software, Inc. (Sentry)</strong>, error tracking.
        <a href="https://sentry.io/privacy/">sentry.io/privacy</a></li>
      <li><strong>Push providers</strong>, Google FCM (Chrome/Edge/Firefox on
        most platforms), Mozilla autopush, Apple Push Notification Service,
        Windows Push Notification Service. These are operated by your
        browser/OS vendor and only receive opaque payloads.</li>
    </ul>

    <h2>4. Cookies and storage</h2>
    <p>StudyVET itself does not set tracking cookies. We use:</p>
    <ul>
      <li><code>localStorage</code> for your theme preference and a small
        set of UI state flags.</li>
      <li><code>IndexedDB</code> to cache the question bank locally so the
        app works offline.</li>
      <li>Firebase Auth's session storage to keep you signed in.</li>
      <li>During checkout, Stripe sets its own cookies on
        <code>checkout.stripe.com</code> per their privacy policy.</li>
    </ul>

    <h2>5. Retention</h2>
    <p>We keep your account data for as long as your account exists. You
    can delete all of your study data at any time from
    <strong>Account → Reset all my progress</strong>. To delete your account
    entirely (including email and any associated billing record), email
    <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a> from the
    address on the account; we'll process the request within 14 days.
    Stripe keeps payment records for as long as their compliance
    obligations require (typically 7 years).</p>

    <h2>6. Your rights</h2>
    <p>Depending on where you live, you may have the right to access,
    correct, export, or delete your personal data. To exercise any of
    these, email <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a>
    from the address on the account. We respond within 30 days.</p>

    <h2>7. Children</h2>
    <p>StudyVET is intended for veterinary students and graduates and is
    not directed to children under 16. If you believe a child has created
    an account, contact us and we will delete it.</p>

    <h2>8. International transfers</h2>
    <p>Our hosting (Firebase, Stripe, Sentry) is operated in the United
    States. By using StudyVET you consent to your data being processed
    there. We do not transfer data to countries without an adequate
    privacy framework as of this writing.</p>

    <h2>9. Changes to this policy</h2>
    <p>If we materially change this policy we will update the "Last updated"
    date and, for substantial changes, notify you via email or an in-app
    banner before the change takes effect.</p>
  `);
}

// ─── Refunds ──────────────────────────────────────────────────────────────
export function renderRefunds(view) {
  view.innerHTML = pageShell("Refund Policy", `
    <p>
      We want StudyVET to genuinely help you. If it doesn't, we'll refund
      you. This policy explains when and how.
    </p>

    <h2>1. 7-day money-back guarantee</h2>
    <p>If you're not happy with your subscription within <strong>7 days</strong>
    of your first payment, email
    <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a> from the address
    on your account and we'll issue a full refund, no questions asked.
    This applies to first-time monthly and annual purchases.</p>

    <h2>2. Renewal charges</h2>
    <p>After the 7-day window, charges for the current billing period
    are non-refundable. We will not auto-renew you without consent, your
    subscription continues automatically based on the plan you chose
    (monthly or annual) until you cancel. To stop the next renewal,
    cancel before the period ends. See
    <a href="#/cancellation">Cancellation Policy</a>.</p>

    <h2>3. Annual plan, mid-year refunds</h2>
    <p>The 7-day money-back guarantee applies to annual plans the same
    way it applies to monthly. After day 7 of an annual plan, we don't
    issue pro-rated refunds for the unused portion. You can still cancel
    at any time and you'll keep access until your period ends.</p>

    <h2>4. Failed payments</h2>
    <p>If a renewal payment fails, your account moves to the "past due"
    state. We retry through Stripe over the following days; if no payment
    succeeds your subscription is canceled and access reverts to the
    free tier. Past-due charges that ultimately fail are not billed
    again retroactively.</p>

    <h2>5. Exceptional circumstances</h2>
    <p>If you've been billed in error, were unable to access the Service
    due to a clear platform issue on our end, or have any other unusual
    situation, email
    <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a>. We review
    every request individually and try to be reasonable.</p>

    <h2>6. How to request a refund</h2>
    <p>Email <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a> from
    the email address on your StudyVET account. Include your full name
    and a brief reason (so we can improve). We respond within 3 business
    days. Approved refunds are processed by Stripe and typically appear
    on your statement within 5–10 days.</p>
  `);
}

// ─── Cancellation ─────────────────────────────────────────────────────────
export function renderCancellation(view) {
  view.innerHTML = pageShell("Cancellation Policy", `
    <p>
      You can cancel your StudyVET Pro subscription at any time. You
      stay in control, we never lock you into a long contract.
    </p>

    <h2>1. How to cancel</h2>
    <p>Sign in to StudyVET, open
    <strong>Account → Manage billing</strong>. That opens Stripe's
    secure customer portal where you can cancel with one click. You'll
    receive an email confirmation from Stripe.</p>
    <p>If you can't access your account, email
    <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a> from the
    address on the account and we'll cancel for you.</p>

    <h2>2. When cancellation takes effect</h2>
    <p>Cancellation stops the next renewal. You keep full Pro access
    until the end of your current billing period. We don't pro-rate
    refunds for unused days, see
    <a href="#/refunds">Refund Policy</a>.</p>

    <h2>3. What happens to your data</h2>
    <p>Your account stays active on the free tier after cancellation.
    All your progress, bookmarks, exam history, and notes are preserved
    so if you re-subscribe in the future, everything is right where you
    left it.</p>
    <p>If you also want your account deleted entirely, email
    <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a>. Account
    deletion is permanent and we cannot recover your study data
    afterwards.</p>

    <h2>4. Reactivating</h2>
    <p>To restart Pro after a cancellation, sign in and visit
    <a href="#/upgrade">Upgrade</a>. The same monthly and annual
    options are available; pricing at the time of resubscription
    applies.</p>

    <h2>5. Cancellation by us</h2>
    <p>We may suspend or cancel a subscription if (a) a payment fails
    repeatedly and we cannot recover it, (b) you violate the Terms of
    Service in a way that materially harms other users or the platform,
    or (c) the Service is discontinued. In case (c), we will refund
    any unused portion of pre-paid time.</p>

    <h2>6. No subscription? No cancellation needed.</h2>
    <p>If you never subscribed (you're on the free tier), there is
    nothing to cancel. Free accounts have no recurring charges and you
    can simply stop using the Service whenever you like.</p>
  `);
}
